// Documento: Cómo dar formato a fecha y hora
// Categoría: Flujos
// Última actualización: 2024-12-28

const document = {
  "id": "formato-fecha-hora",
  "title": "Cómo dar formato a fecha y hora",
  "category": "Flujos",
  "description": "Aprende a formatear fechas y horas en tus flujos para adaptarte a diferentes países y regiones. La plataforma Runamatic permite personalizar el formato de fecha y hora según tus necesidades específicas.",
  "explanations": [
    {
      "title": "¿Por qué formatear fechas y horas?",
      "content": "Dar formato a la fecha y hora es útil para quienes tienen clientes en diferentes países y regiones, ya que el formato de fecha y hora no es el mismo en todos lados. En la plataforma Runamatic, puedes cambiar el formato de la fecha y hora según tus necesidades, mejorando la experiencia del usuario y la comprensión de la información temporal."
    },
    {
      "title": "Opciones de zona horaria",
      "content": "Además del formato, puedes elegir la zona horaria de esta fecha y hora formateada, ya sea la de la cuenta propia o la del usuario. Esto es especialmente útil cuando trabajas con clientes internacionales que se encuentran en diferentes husos horarios."
    },
    {
      "title": "Campos personalizados para fechas",
      "content": "El sistema te permite seleccionar campos personalizados de fecha existentes que contienen la fecha que deseas formatear, y también crear nuevos campos personalizados para guardar el resultado del formato aplicado."
    },
    {
      "title": "Ejemplos de formato",
      "content": "Por ejemplo, si quieres mostrar la fecha y hora como \"23-01-2022; 02-00 a.m.\" entonces debes ingresar en el campo de formato: `d-m-Y; h-i a`. La tabla de parámetros te ayudará a construir el formato exacto que necesitas."
    }
  ],
  "steps": [
    {
      "title": "Acceder al flujo",
      "content": "Ve al flujo que deseas editar desde la sección de Flujos en el menú principal de la plataforma."
    },
    {
      "title": "Seleccionar la acción Format Date",
      "content": "Entre las muchas opciones del constructor, elige la opción \"Action\" y selecciona \"Format Date/Date & Time\" de la lista de acciones disponibles."
    },
    {
      "title": "Configurar el campo de fecha origen",
      "content": "Selecciona el campo personalizado de fecha que contiene la fecha que deseas formatear. Este debe ser un campo que ya contenga información de fecha válida."
    },
    {
      "title": "Definir el formato deseado",
      "content": "En el siguiente campo, ingresa el formato de la fecha y hora que deseas aplicar. Utiliza los parámetros de la tabla de referencia para construir tu formato personalizado. Por ejemplo: `d-m-Y; h-i a` para obtener \"23-01-2022; 02-00 a.m.\"."
    },
    {
      "title": "Configurar el campo de destino",
      "content": "Crea un nuevo campo personalizado o selecciona uno existente para guardar el formato de fecha y hora en el campo \"Save the formatted date to\"."
    },
    {
      "title": "Seleccionar zona horaria",
      "content": "Elige la zona horaria para esta fecha y hora formateada, ya sea la de la cuenta propia o la del usuario, según tus necesidades específicas."
    }
  ],
  "table": {
    "title": "Parámetros de formatos de fecha y hora",
    "headers": ["Carácter", "Descripción", "Valores"],
    "rows": [
      ["**Día**", "—", "—"],
      ["`d`", "Día del mes, 2 dígitos con ceros a la izquierda", "01 al 31"],
      ["`D`", "Representación textual corta del día (3 letras)", "Lun a Dom"],
      ["`j`", "Día del mes sin ceros a la izquierda", "1 al 31"],
      ["`l` (letra \"L\" minúscula)", "Nombre completo del día de la semana", "Domingo a Sábado"],
      ["`N`", "Representación numérica ISO-8601 del día de la semana", "1 (lunes) a 7 (domingo)"],
      ["`S`", "Sufijo ordinal en inglés para el día del mes (2 caracteres)", "st, nd, rd o th (funciona con j)"],
      ["`w`", "Representación numérica del día de la semana", "0 (domingo) a 6 (sábado)"],
      ["`z`", "Día del año (comenzando en 0)", "0 a 365"],
      ["**Semana**", "—", "—"],
      ["`W`", "Número ISO-8601 de la semana del año (comenzando en lunes)", "Ejemplo: 42 (semana 42 del año)"],
      ["**Mes**", "—", "—"],
      ["`F`", "Nombre completo del mes", "Enero a Diciembre"],
      ["`m`", "Número del mes, con ceros a la izquierda", "01 al 12"],
      ["`M`", "Abreviatura del mes (3 letras)", "Ene a Dic"],
      ["`n`", "Número del mes, sin ceros a la izquierda", "1 al 12"],
      ["`t`", "Número de días en el mes", "28 a 31"],
      ["**Año**", "—", "—"],
      ["`L`", "Si es un año bisiesto", "1 si lo es, 0 si no"],
      ["`o`", "Año según la numeración ISO-8601 de la semana (puede diferir de Y)", "Ej.: 1999 o 2003"],
      ["`Y`", "Representación numérica completa del año (4 dígitos)", "Ej.: 1999 o 2003"],
      ["`y`", "Representación numérica corta del año (2 dígitos)", "Ej.: 99 o 03"],
      ["**Hora**", "—", "—"],
      ["`a`", "Ante meridiem y Post meridiem en minúsculas", "am o pm"],
      ["`A`", "Ante meridiem y Post meridiem en mayúsculas", "AM o PM"],
      ["`g`", "Formato de 12 horas sin ceros a la izquierda", "1 a 12"],
      ["`G`", "Formato de 24 horas sin ceros a la izquierda", "0 a 23"],
      ["`h`", "Formato de 12 horas con ceros a la izquierda", "01 a 12"],
      ["`H`", "Formato de 24 horas con ceros a la izquierda", "00 a 23"],
      ["`i`", "Minutos con ceros a la izquierda", "00 a 59"],
      ["`s`", "Segundos con ceros a la izquierda", "00 a 59"],
      ["`U`", "Segundos desde la Época Unix (1 enero 1970 00:00:00 GMT). Muy importante porque permite obtener la fecha en segundos totales, con lo que se pueden hacer cálculos. Ejemplo: un bot de cuestionarios puede guardar la fecha de inicio y fin del usuario, y calcular el tiempo en minutos con `({{stop}} – {{start}})/60`.", "Timestamp numérico"]
    ]
  },
  "tags": [
    "fecha",
    "hora",
    "formato",
    "timezone",
    "campos-personalizados",
    "internacionalización",
    "zona-horaria",
    "timestamp"
  ],
  "lastUpdated": "2024-12-28",
  "difficulty": "intermedio"
};

export default document;
